import { Component, ElementRef } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import {  AuthService } from '@usimweb/core-auth';
import { ApiService } from '@usimweb/core/services';
import { Annotation, Comments } from '@usimweb/core/models';
import { BehaviorSubject, Observable } from 'rxjs';

interface IDialogService {
  openTextDialog(component: any, data: any, title: string, saveBtn?: string, text?: string): Observable<any>;
  getDialog(dialog: string): Component;
}

export enum CommentType {
  NEW = 'new',
  CURRENT = 'current',
}

export abstract class Chat {
  private version_: any;
  private dialogService_!: IDialogService;

  protected activeComment: boolean = false;

  protected loadVersionListener: BehaviorSubject<any> = new BehaviorSubject('');
  protected resetValues: BehaviorSubject<boolean> = new BehaviorSubject(false);

  get project_uuid() {
    return this.version_['project'];
  }

  get version_uuid() {
    return this.version_['uuid'];
  }

  public newComment!: any;
  public selectedColor: string = 'white';
  public currentUser = '';

  public comments!: Comments[];
  public commentTypeEnum = CommentType;
  public currentAnnos!: Annotation[];
  public selectedAnnotation: number = -1;
  public isAnnotationMode: boolean = false;

  constructor(private apiService_: ApiService, private snackBar_: MatSnackBar) {
    const user = AuthService.basfUserName;
    this.currentUser = user?.toLocaleLowerCase() || '';
  }

  abstract enablePicker(event: MouseEvent, commentType: CommentType, annotationindex: number): void;
  abstract deleteAnnotation(index: number, comment?: Comments): void;
  abstract selectAnnotation(event: any, index: number, comment?: Comments): void;

  private createCommentObj(content: any, color: string, annos: any, scalar: string, version: any) {
    const comment = {
      content: content,
      color: color,
      meta: {
        annotations: annos,
        scalar: scalar,
        version: version,
      },
      tags: ['v_' + version.uuid],
    };
    return comment;
  }

  protected getComments() {
    this.apiService_.getComments(this.project_uuid).subscribe((comments) => {
      this.comments = comments;
    });
  }

  protected addComment(active_scalar: string = '') {
    if (this.newComment !== undefined && this.newComment.trim() !== '') {
      const comment = this.createCommentObj(this.newComment, this.selectedColor, this.currentAnnos, active_scalar, this.version_);

      this.apiService_.addComments(comment, this.project_uuid).subscribe((comments) => {
        this.comments = comments;
        //this.snackBar.open('Added new comment', 'OK', { duration: 2000 });
        this.newComment = '';
        this.resetValues.next(true);
        this.selectedColor = 'white';

        this.selectComment(this.comments[this.comments.length - 1]);
      });
    }
  }

  protected initProps(version: any, dialogService: any) {
    this.version_ = version;
    this.dialogService_ = dialogService;
  }

  protected updateVersion(version: any) {
    this.version_ = version;
  }

  public comment(active_scalar: string = '') {
    this.addComment(active_scalar);
  }

  public updateComment(event: MouseEvent, comment: Comments, content: string, active_scalar: string = '') {
    event.stopPropagation();
    comment.editing = false;
    comment.content = content;

    const updated_comment = this.createCommentObj(content, comment.color, comment.meta?.annotations, active_scalar, this.version_);

    this.apiService_.updateComment(updated_comment, this.project_uuid, comment.uuid, this.version_uuid).subscribe((comments) => {
      this.comments = comments;
      this.snackBar_.open('Updated comment', 'OK', { duration: 2000 });
      this.selectComment(this.comments[this.comments.length - 1]);

    });
  }

  public deleteComment(comment: Comments) {
    this.dialogService_
      .openTextDialog(this.dialogService_.getDialog('InfoDialogComponent'), {}, 'Delete comment', 'Delete', 'Are you sure you want to delete?')
      .subscribe((data: any) => {
        if (data) {
          this.apiService_.deleteComment(this.project_uuid, comment.uuid).subscribe((comments) => {
            this.comments = comments;
            this.resetValues.next(true);
            this.snackBar_.open('Removed comment...', 'OK', { duration: 2000 });
          });
        }
      });
  }

  public addReply(event: MouseEvent, comment: Comments): void {
    //event.stopPropagation();

    let uuid = comment.uuid;
    if (comment.replies.length > 0) {
      uuid = comment.replies.pop()?.uuid || '';
    }
    this.apiService_.addReply(this.project_uuid, uuid, this.version_.uuid, comment.reply_content || '').subscribe((commments) => {
      this.comments = commments;
      this.snackBar_.open('Added reply...', 'OK', { duration: 2000 });
    });
  }

  public deleteReply(reply: any): void {
    this.dialogService_
      .openTextDialog(this.dialogService_.getDialog('InfoDialogComponent'), {}, 'Delete reply', 'Delete', 'Are you sure you want to delete?')
      .subscribe((data: any) => {
        if (data) {
          this.apiService_.deleteComment(this.project_uuid, reply.uuid).subscribe((comments) => {
            this.comments = comments;
            this.resetValues.next(true);
            this.snackBar_.open('Removed reply...', 'OK', { duration: 2000 });
          });
        }
      });
  }

  public editReply(reply: Comments): void {
    reply.editing = !reply.editing;
    this.resetValues.next(true);
  }

  public showReplyForm(event: any, comment: Comments): void {
    event.stopPropagation();
    comment.replying = !comment.replying;
    // if (comment.replying) {
    //   this.selectComment(comment);
    // }
  }

  public updateReply(event: MouseEvent, reply: Comments, content: string) {
    event.stopPropagation();
    reply.formatted_date = new Date().toLocaleString();
    reply.editing = false;

    this.apiService_.updateReply(content, this.project_uuid, reply.uuid, this.version_uuid).subscribe((comments) => {
      this.comments = comments;
      this.snackBar_.open('Updated reply...', 'OK', { duration: 2000 });
    });
  }

  //allow to edit color of annotation chat & annotations
  public editComment(comment: Comments) {
    if (comment.editable) {
      comment.color = comment.lastColor || '';
    }
    comment.editing = !comment.editing;
    this.resetValues.next(true);
    if (comment.editing) {
      this.selectComment(comment);
    }
  }

  public selectComment(comment: Comments) {
    this.activeComment = true;

    this.comments.forEach((comment) => { comment.selected = false; });

    comment.selected = true;
    //if (!comment.editing && !comment.replying) {
    const scalar = comment.meta?.scalar;

    const length = comment.meta?.annotations?.length || 0;

    if (length > 0) {
      const annotations = comment.meta?.annotations;

      this.loadVersionListener.next({ version: comment.meta?.version, scalar: scalar?.name, annos: annotations });
    } else {
      this.loadVersionListener.next({ version: comment.meta?.version, scalar: scalar?.name });
    }
    //}
  }

  public changeCommentColor(comment: Comments, color: string) {
    comment.lastColor = comment.color;
    comment.color = color;
  }

  public isAnnos(comment: any) {
    return comment.meta?.annotations?.length > 0 ? true : false;
  }

  public getAnnos(comment: any) {
    return comment.meta.annotations;
  }

  public cancelClickEvent(event: MouseEvent) {
    event.stopPropagation();
  }

  public deselectComment() {
    if (this.activeComment) {
      this.resetValues.next(true);
      this.activeComment = false;
    }
  }
}
